import { endOfDay, subDays } from 'date-fns';
import type { DateRange, UnifiedActivity } from '@/lib/api/activity-sync-schema';
import { stravaClient } from '@/lib/api/strava-client';
import { getUnifiedIntervalsActivitiesInDateRange } from '@/lib/api/intervals-sync';
import { createLogger } from '@/lib/logger';
import { useStore } from '@/lib/store';
import { useSyncStore } from '@/lib/store/sync-store';
import { supabase } from '@/lib/supabase';
import { buildLast365DayChunks } from './activity-sync-chunks';

const log = createLogger('activity-sync-service');
const HISTORICAL_SYNC_DELAY_MS = 500;

type SyncCursorRow = {
    last_synced_at?: string | null;
    last_sync_cursor?: string | null;
};

function parseDate(value: string): Date | null {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function hasConnectedActivityProvider(): boolean {
    const state = useStore.getState();
    return Boolean(state.strava.accessToken || state.intervals.accessToken);
}

function filterToRange(
    activities: UnifiedActivity[],
    range: DateRange,
    strictAfter?: Date
): UnifiedActivity[] {
    const rangeStart = range.start.getTime();
    const rangeEnd = range.end.getTime();
    const strictAfterTs = strictAfter?.getTime() ?? null;

    return activities.filter((activity) => {
        const activityDate = parseDate(activity.start_date);
        if (!activityDate) return false;

        const activityTs = activityDate.getTime();
        if (activityTs > rangeEnd) return false;
        if (strictAfterTs !== null) return activityTs > strictAfterTs;
        return activityTs >= rangeStart;
    });
}

function dedupeActivities(activities: UnifiedActivity[]): UnifiedActivity[] {
    return Array.from(
        new Map(activities.map((activity) => [activity.external_id, activity])).values()
    );
}

async function upsertActivities(activities: UnifiedActivity[]): Promise<void> {
    if (activities.length === 0) return;

    const { error } = await supabase
        .from('activities')
        .upsert(activities, { onConflict: 'external_id' });

    if (error) {
        throw new Error(`Failed to upsert activities: ${error.message}`);
    }
}

async function fetchUnifiedActivitiesForRange(
    athleteId: string,
    range: DateRange,
    options: { strictAfter?: Date } = {}
): Promise<UnifiedActivity[]> {
    const state = useStore.getState();
    const before = endOfDay(range.end);
    const afterForStrava = options.strictAfter
        ? options.strictAfter
        : new Date(range.start.getTime() - 1000);

    const results: UnifiedActivity[] = [];

    if (state.strava.accessToken) {
        const stravaActivities = await stravaClient.getUnifiedActivitiesInDateRange(athleteId, {
            after: afterForStrava,
            before,
        });
        results.push(...filterToRange(stravaActivities, { start: range.start, end: before }, options.strictAfter));
    }

    if (state.intervals.accessToken) {
        const intervalsActivities = await getUnifiedIntervalsActivitiesInDateRange(athleteId, {
            after: range.start,
            before,
        });
        results.push(...filterToRange(intervalsActivities, { start: range.start, end: before }, options.strictAfter));
    }

    return dedupeActivities(results);
}

async function getLastSyncedAt(athleteId: string): Promise<Date | null> {
    const { data, error } = await supabase
        .from('athlete_sync_state')
        .select('last_synced_at,last_sync_cursor')
        .eq('athlete_id', athleteId)
        .maybeSingle();

    if (error) {
        log.warn('cursor', 'Failed to load sync cursor, using 30-day fallback', { error });
        return null;
    }

    const cursor = (data as SyncCursorRow | null)?.last_synced_at
        ?? (data as SyncCursorRow | null)?.last_sync_cursor
        ?? null;

    if (!cursor) return null;
    return parseDate(cursor);
}

async function saveSyncCursor(
    athleteId: string,
    cursor: Date,
    options: { markFullSync?: boolean } = {}
): Promise<void> {
    const cursorIso = cursor.toISOString();
    const payload: Record<string, string> = {
        athlete_id: athleteId,
        last_sync_cursor: cursorIso,
        last_synced_at: cursorIso,
        updated_at: cursorIso,
    };

    if (options.markFullSync) {
        payload.last_full_sync = cursorIso;
    }

    const { error } = await supabase
        .from('athlete_sync_state')
        .upsert(payload, { onConflict: 'athlete_id' });

    if (error) {
        log.warn('cursor', 'Failed to persist sync cursor', { error, athleteId });
    }
}

export async function runIncrementalActivitySync(athleteId: string): Promise<number> {
    if (!hasConnectedActivityProvider()) {
        return 0;
    }

    const lastSyncedAt = await getLastSyncedAt(athleteId);
    const fallbackCursor = subDays(new Date(), 30);
    const cursor = lastSyncedAt ?? fallbackCursor;
    const syncEnd = new Date();
    const range = { start: cursor, end: syncEnd };
    const activities = await fetchUnifiedActivitiesForRange(athleteId, range, {
        strictAfter: cursor,
    });

    await upsertActivities(activities);
    await saveSyncCursor(athleteId, syncEnd);
    return activities.length;
}

export async function runHistoricalYearSync(athleteId: string): Promise<number> {
    if (!hasConnectedActivityProvider()) {
        return 0;
    }

    const chunks = buildLast365DayChunks(new Date());
    const syncState = useSyncStore.getState();
    let totalSynced = 0;

    syncState.startHistoricalSync();

    try {
        for (let index = 0; index < chunks.length; index += 1) {
            useSyncStore.getState().setHistoricalSyncProgress(index);

            const chunk = chunks[index];
            const activities = await fetchUnifiedActivitiesForRange(athleteId, chunk);
            await upsertActivities(activities);
            totalSynced += activities.length;

            useSyncStore.getState().setHistoricalSyncProgress(index + 1);

            if (index < chunks.length - 1) {
                await new Promise((resolve) => setTimeout(resolve, HISTORICAL_SYNC_DELAY_MS));
            }
        }

        await saveSyncCursor(athleteId, new Date(), { markFullSync: true });
        useSyncStore.getState().finishHistoricalSync();
        return totalSynced;
    } catch (error) {
        useSyncStore.getState().resetHistoricalSync();
        throw error;
    }
}

